import React from 'react'
import { v4 } from 'uuid'
import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { getArrayValue } from '@commutifi-fe/utils'
import { LabeledValue, BaseOptionType, DefaultOptionType } from '../AppSelect'
import { BaseFetchSelect, BaseFetchSelectProps, OmitBaseSelectProps, FetchSelectType } from './BaseFetchSelect'

export { FetchSelectType }

export interface FetchSelectProps<
  QueryResponse,
  Data = QueryResponse,
  ValueType = string,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
> extends Omit<BaseFetchSelectProps<'Single', Data, never, ValueType, OptionType>, OmitBaseSelectProps> {
  // Function that will be called with array of params (see deps prop).
  // Should be called once unless you pass some dependencies (deps property)
  query: (...params: unknown[]) => Promise<QueryResponse> | QueryResponse
  // Uniq Query key to identify the query
  queryKey?: QueryKey | string
  // Parameters that will trigger a refetch when their hash changes
  queryParams?: unknown | unknown[]
  // Options to use in the hook useAsync we use to call APIs
  queryOptions?: Partial<UseQueryOptions<QueryResponse, Error, Data, QueryKey>>
}

/**
 * Use this component when you need to instantiate a Select component
 * that will need to fetch and display some data in which you will be able
 * to search and select values part of the normalized request answer
 * @param props - {@link FetchSelectProps}
 */
export function FetchSelect<
  QueryResponse,
  Data = QueryResponse,
  ValueType extends string | number | string[] | number[] | LabeledValue | LabeledValue[] = string,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>({
  query = () => Promise.resolve({} as QueryResponse),
  queryKey = v4(),
  queryParams,
  queryOptions,
  isProcessing,
  ...rest
}: FetchSelectProps<QueryResponse, Data, ValueType, OptionType>) {
  // Combine key and params to make sure 2 query with the same id but different params
  // are not treated as the same instance
  const key = React.useMemo(() => {
    const queryKeyArray = getArrayValue<unknown | unknown[] | undefined>(queryKey)
    const queryParamsArray = getArrayValue<unknown | unknown[] | undefined>(queryParams)
    return [...queryKeyArray, ...queryParamsArray]
  }, [queryKey, queryParams])

  const { data, isFetching, isLoading, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => query(...getArrayValue(queryParams)),
    ...queryOptions
  })

  return (
    <BaseFetchSelect<'Default', Data, never, ValueType, OptionType>
      refetch={refetch}
      data={data}
      error={error}
      isLoading={isLoading}
      isProcessing={isProcessing || isFetching}
      {...rest}
    />
  )
}

export default FetchSelect
